import { type FC } from 'react';

import { Divider } from '../Divider';
import { type IconSize, type IconVariantTypes, Icon } from '../Icon';
import { Text } from '../Typography';
import {
  copyStyles,
  detailCopyStyles,
  detailIconStyles,
  detailIconTileStyles,
  detailRowMultilineStyles,
  detailRowStyles,
  detailsListStyles,
  detailsListTileStyles,
  dividerStyles,
  editButtonStyles,
  footerStyles,
  headerStyles,
  rootStyles,
  thumbnailStyles,
  variantStyles,
} from './TripSummaryCard.styles';
import type { TripSummaryCardProps } from './TripSummaryCard.types';

/**
 * People icons the card may be given for a guest row.
 * Activity icons such as PersonHiking stay as the consumer passed them.
 */
const guestIconVariants: ReadonlySet<IconVariantTypes> = new Set([
  'User',
  'Users',
  'UserSolid',
]);

const resolveDetailIcon = (
  icon: IconVariantTypes
): { variant: IconVariantTypes; size: IconSize } => ({
  variant: guestIconVariants.has(icon) ? 'User' : icon,
  size: 'large',
});

const TripSummaryCard: FC<TripSummaryCardProps> = ({
  image,
  title,
  subtitle,
  variant = 'default',
  details,
  iconStyle = 'plain',
  onEdit,
  editLabel = 'Edit',
  className,
  ...props
}) => {
  const visibleDetails = details ?? [];
  const showFooter = visibleDetails.length > 0 || Boolean(onEdit);

  return (
    <div
      {...props}
      className={className}
      css={[rootStyles, variantStyles[variant]]}
    >
      <div css={headerStyles}>
        <img css={thumbnailStyles} src={image.src} alt={image.alt} />
        <div css={copyStyles}>
          <Text size="md" fontWeight="bold" color="var(--text-primary)">
            {title}
          </Text>
          <Text size="sm" color="var(--text-secondary)">
            {subtitle}
          </Text>
        </div>
      </div>

      {showFooter && (
        <>
          <Divider aria-hidden="true" css={dividerStyles} />
          <div css={footerStyles}>
            {visibleDetails.length > 0 && (
              <ul
                css={
                  iconStyle === 'tile'
                    ? [detailsListStyles, detailsListTileStyles]
                    : detailsListStyles
                }
              >
                {visibleDetails.map((detail, index) => {
                  const { variant, size } = resolveDetailIcon(detail.icon);
                  const isMultiline =
                    detail.secondaryLabel != null || detail.description != null;

                  return (
                    <li
                      key={`${detail.icon}-${detail.label}-${index}`}
                      css={
                        isMultiline
                          ? [detailRowStyles, detailRowMultilineStyles]
                          : detailRowStyles
                      }
                    >
                      <span
                        data-icon={variant}
                        data-icon-size={size}
                        css={
                          iconStyle === 'tile'
                            ? [detailIconStyles, detailIconTileStyles]
                            : detailIconStyles
                        }
                      >
                        <Icon variant={variant} size={size} />
                      </span>
                      {isMultiline ? (
                        <span css={detailCopyStyles}>
                          <Text
                            as="span"
                            size="sm"
                            fontWeight="bold"
                            color="var(--text-primary)"
                          >
                            {detail.label}
                          </Text>
                          {detail.secondaryLabel != null && (
                            <Text
                              as="span"
                              size="sm"
                              color="var(--text-primary)"
                            >
                              {detail.secondaryLabel}
                            </Text>
                          )}
                          {detail.description != null && (
                            <Text
                              as="span"
                              size="xs"
                              color="var(--text-secondary)"
                            >
                              {detail.description}
                            </Text>
                          )}
                        </span>
                      ) : (
                        <Text as="span" size="sm" color="var(--text-primary)">
                          {detail.label}
                        </Text>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {onEdit && (
              <button type="button" css={editButtonStyles} onClick={onEdit}>
                {editLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

TripSummaryCard.displayName = 'TripSummaryCard';

export default TripSummaryCard;
